// App.js
import React, { useEffect } from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import { Amplify } from "aws-amplify";

import "@aws-amplify/ui-react/styles.css";
import config from "./amplifyconfiguration.json";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

import DrawerComponent from "./components/Drawer";
import "./index.css";
import { ToastContainer } from "react-toastify";

import CustomSignupForm from "./pages/signup";
import { signOut } from "aws-amplify/auth";
import Login from "./pages/login";
import ProfileCreateForm from "./pages/user";
import PaymentDetail from "./pages/paymentDetail";
import AddPaymentDetails from "./pages/addPaymentDetails";
import { ContentUpdateForm } from "./ui-components";
import ContentCreationStepper from "./components/ContentCreationStepper";
import CreateContent from "./pages/createContent";
import Cycles from "./pages/cycles";
import ViewContent from "./pages/viewContent";
import ContentGuidelines from "./pages/content-guidelines";
import AgreementPreviewPage from "./pages/agreementPreviewPage";
import Analytics from "./pages/analytics";
import DashboardConfig from "./pages/dashboardconfig";
import SecuritySettings from "./pages/settings";
import ContentProviderDashboard from "./components/ContentProviderDashboard";
import Agreements from "./pages/agreements";
import Content from "./pages/content";
import Manifest from "./pages/manifest";
import ProtectedRoute from "./components/ProtectedRoute";
import { useDispatch, useSelector } from "react-redux";
import { setAuthenticatedUser, setAuthLoading } from "./redux/slices/user";
import VerifyEmail from "./pages/verifyEmail";
import ContentProviders from "./pages/contentProviders";
import Payments from "./pages/payments";
import AdminProviderDashboard from "./pages/adminProviderDashboard";
import { AUTHENTICATED, UNAUTHENTICATED } from "./constants";
import { fetchUserAttributes } from "aws-amplify/auth";
import BatchContentReview from "./pages/batchContentReview";
import ContentsPerBatch from "./pages/contentsPerBatch";
import EditContentStepper from "./components/EditContentStepper";
import EditAgreementStepper from "./components/EditAgreementStepper";
import ManifestItems from "./pages/manifestItems";
import VerifyOTP from "./pages/verifyOTP";
import TermsAndConditions from "./pages/termsConditions";
import ManifestUpdateForm from "./ui-components/ManifestUpdateForm";
import AddManifestItems from "./pages/addManifestItems";
import Albums from "./pages/albums";
import AgreementFormV2 from "./components/AgreementTerms/AgreementFormV2";
import Series from "./pages/series";
import Seasons from "./pages/seasons";
import TokenHandler from "./TokenHandler";

Amplify.configure(config);

export default function App() {
	const user = useSelector((state) => state.auth.user);
	const dispatch = useDispatch();

	const handleSignOut = () => {
		localStorage.removeItem("token");
		localStorage.removeItem("userProfileId");
		localStorage.removeItem("loggedInUserGroups");
		localStorage.removeItem("attributes");

		signOut();
		dispatch(
			setAuthenticatedUser({
				authState: UNAUTHENTICATED,
				role: "",
				attributes: {},
			})
		);
	};

	useEffect(() => {
		const getAuthenticatedUser = async () => {
			try {
				dispatch(setAuthLoading("loading"));
				const { tokens: session } = await fetchAuthSession();
				console.log(session, "tokens");
				const groups =
					session.accessToken.payload["cognito:groups"] || [];

				const attributes = await fetchUserAttributes();

				if (session.accessToken) {
					dispatch(
						setAuthenticatedUser({
							authState: AUTHENTICATED,
							role: groups[0],
							attributes: attributes,
						})
					);
				}
				localStorage.setItem(
					"loggedInUserGroups",
					JSON.stringify(groups)
				);
				localStorage.setItem("token", JSON.stringify(session));
			} catch (error) {
				console.error("Error fetching user data:", error);
				dispatch(
					setAuthenticatedUser({
						authState: UNAUTHENTICATED,
						role: "",
						attributes: null,
					})
				);
			}
		};
		if (user?.authState === UNAUTHENTICATED) getAuthenticatedUser();
	}, []);

	return (
		<>
			<TokenHandler />
			<Router>
				<Routes>
					<Route path="/signup" element={<CustomSignupForm />} />
					<Route path="/login" element={<Login />} />
					<Route
						path="/verify-email/:email"
						element={<VerifyEmail />}
					/>

					<Route path="/verify-otp" element={<VerifyOTP />} />
					<Route
						element={
							<ProtectedRoute>
								<DrawerComponent
									handleSignOut={handleSignOut}
								/>
							</ProtectedRoute>
						}
					>
						{/* Routes remain the same */}
						<Route
							path="/"
							element={
								<ProtectedRoute>
									{user.role === "ContentProviders" ? (
										<ContentProviderDashboard />
									) : (
										<AdminProviderDashboard />
									)}
								</ProtectedRoute>
							}
						/>
						<Route
							path="/dashboard-configuration"
							element={<DashboardConfig />}
						/>
						<Route path="/content" element={<Content />} />
						<Route path="/tv-series-review" element={<Content />} />
						<Route path="/series" element={<Series/>} />
						<Route path="/seasons" element={<Seasons/>} />
						
						<Route path="/albums-review" element={<Albums />} />
						<Route
							path="/batch-content-review"
							element={<BatchContentReview />}
						/>
						<Route
							path="/batch-content-review/:id"
							element={<ContentsPerBatch />}
						/>

						<Route path="/manifest" element={<Manifest />} />
						<Route
							path="/addItems"
							element={<AddManifestItems />}
						/>

						<Route
							path="/manifest/manifest/update"
							element={<ManifestUpdateForm />}
						/>

						<Route path="/cycles" element={<Cycles />} />
						<Route path="/analytics" element={<Analytics />} />
						<Route
							path="/security-settings"
							element={<SecuritySettings />}
						/>
						<Route
							path="/terms-conditions"
							element={<TermsAndConditions />}
						/>

						<Route path="/content/:id" element={<ViewContent />} />
						<Route path="/agreements" element={<Agreements />} />
						<Route
							path="/agreement/:id"
							element={<AgreementPreviewPage />}
						/>
						<Route
							path="/content-guidelines"
							element={<ContentGuidelines />}
						/>

						<Route
							path="/content/create-content"
							element={<CreateContent />}
						/>
						<Route
							path="/add-new-agreement"
							element={<AgreementFormV2 />}
						/>
						<Route
							path="/add-new-content"
							element={<ContentCreationStepper />}
						/>
						<Route
							path="/agreement/:id/cycle/:currentCycle/:availableCycle"
							element={<ContentCreationStepper />}
						/>
						<Route
							path="/content/update"
							element={<ContentUpdateForm />}
						/>
						<Route
							path="/add-payment-details"
							element={<AddPaymentDetails />}
						/>
						<Route path="/payments" element={<Payments />} />
						<Route
							path="/payment/:id"
							element={<PaymentDetail />}
						/>
						<Route path="/user" element={<ProfileCreateForm />} />
						<Route
							path="/content-providers"
							element={<ContentProviders />}
						/>
						<Route
							path="/content/edit/:agreementId/:contentId"
							element={<EditContentStepper />}
						/>
						<Route
							path="/agreement/edit/:agreementId"
							element={<EditAgreementStepper />}
						/>
						<Route
							path="/manifest/manifest/items"
							element={<ManifestItems />}
						/>
					</Route>
				</Routes>
			</Router>
			<ToastContainer
				position="top-right"
				autoClose={5000}
				hideProgressBar={false}
				newestOnTop={false}
				closeOnClick
				rtl={false}
				pauseOnFocusLoss
				theme="colored"
			/>
		</>
	);
}